<script setup>

import Button from "primevue/button";
import Select from "primevue/select";
import DatePicker from "primevue/datepicker";
import Popover from 'primevue/popover';
import Avatar from 'primevue/avatar';

import {onMounted, ref} from "vue";
import { trans } from 'laravel-vue-i18n';
import ReverseCheckbox from "./ReverseCheckbox.vue";

const props = defineProps({
    init: Boolean,
    worker: Boolean,
    workers: Array,
    mobile: Boolean,
})

const op = ref()
const currentTimeSlot = ref()
const toggleWorkers = (event, timeSlot) => {
    currentTimeSlot.value = timeSlot;
    op.value.toggle(event)
}

const daySlots = defineModel();

const addDaySlot = () => {
    const daysTemplate = {
        valid: {
            from: null,
            to: null
        },
        days: {
            mon: {
                id: 'mon',
                label: trans('common.dow_long.mon'),
                selected: false,
                timeslots: []
            },
            tue: {
                id: 'tue',
                label: trans('common.dow_long.tue'),
                selected: false,
                timeslots: []
            },
            wed: {
                id: 'wed',
                label: trans('common.dow_long.wed'),
                selected: false,
                timeslots: []
            },
            thu: {
                id: 'thu',
                label: trans('common.dow_long.thu'),
                selected: false,
                timeslots: []
            },
            fri: {
                id: 'fri',
                label: trans('common.dow_long.fri'),
                selected: false,
                timeslots: []
            },
            sat: {
                id: 'sat',
                label: trans('common.dow_long.sat'),
                selected: false,
                timeslots: []
            },
            sun: {
                id: 'sun',
                label: trans('common.dow_long.sun'),
                selected: false,
                timeslots: []
            }
        }
    }
    daySlots.value.push(daysTemplate);
}

const addTimeSlot = (container) => {
    container.push({
        time_start: null,
        time_end: null,
        blocking: true,
        exclude_workers: []
    });
}

onMounted(() => {
    if(props.init && daySlots.value.length === 0) addDaySlot()
})

const timeToMinutes = (time) => {
    if (!time) {
        throw new Error("Invalid time provided");
    }
    const [hours, minutes] = time?.split(':').map(Number);
    return hours * 60 + minutes;
}

const minutesToTime = (minutes) => {
    const hours = Math.floor(minutes / 60).toString().padStart(2, '0');
    const mins = (minutes % 60).toString().padStart(2, '0');
    return `${hours}:${mins}`;
}

const getValidStartTimes = (timeslots, excludeSlot = null, currentSlot) => {
    const validStartTimes = [];

    // Pokud existuje timeslot, který chceme vyloučit, odstraníme ho dočasně ze seznamu
    const filteredTimeslots = excludeSlot ? timeslots.filter(slot => slot !== excludeSlot) : timeslots;

    const dayStart = 0; // 00:00
    const dayEnd = 24 * 60; // 24:00

    for (let time = dayStart; time < dayEnd; time += 30) {
        const currentTime = minutesToTime(time);

        // Zkontrolujeme, zda je časový slot platný
        const isValid = filteredTimeslots.length === 0 ? true : !filteredTimeslots.some(slot => {
            if (slot === currentSlot) {
                return false; // Aktuální slot se ignoruje
            }
            if (!slot.time_start || !slot.time_end) {
                return false; // Slot s null se ignoruje
            }
            const slotStart = timeToMinutes(slot.time_start);
            const slotEnd = timeToMinutes(slot.time_end);
            return time >= slotStart && time < slotEnd;
        });

        if (isValid) {
            validStartTimes.push({
                value: currentTime,
                label: currentTime
            });
        }
    }

    return validStartTimes;
}

const getValidEndTimes = (timeslots, startTime, excludeSlot = null, currentSlot) => {
    const validEndTimes = [];

    // Pokud existuje timeslot, který chceme vyloučit, odstraníme ho dočasně ze seznamu
    const filteredTimeslots = excludeSlot ? timeslots.filter(slot => slot !== excludeSlot) : timeslots;

    const startMinutes = timeToMinutes(startTime);
    const dayEnd = 24 * 60; // 24:00

    for (let time = startMinutes + 30; time <= dayEnd; time += 30) {
        const endTime = minutesToTime(time);
        const isValid = filteredTimeslots.length === 0 ? true : !filteredTimeslots.some(slot => {
            if (slot === currentSlot) {
                return false; // Aktuální slot se ignoruje
            }
            if (!slot.time_start || !slot.time_end) {
                return false; // Slot s null se ignoruje
            }
            const slotStart = timeToMinutes(slot.time_start);
            const slotEnd = timeToMinutes(slot.time_end);
            return (time > slotStart && startMinutes < slotEnd);
        });

        if (isValid) {
            const diffInMinutes = time - startMinutes;
            const diffInHours = `${Math.floor(diffInMinutes / 60)}:${(diffInMinutes % 60).toString().padStart(2, '0')}`;

            validEndTimes.push({
                value: endTime,
                label: `${endTime} (${diffInHours} h)`
            });
        }
    }

    return validEndTimes;
}
</script>

<template>
    <div v-for="(daySlot, i) in daySlots" :key="`${i}-daySlot`" class="mt-4 bg-white border shadow-sm overflow-hidden dark:bg-transparent dark:border-neutral-700 rounded-lg w-full overflow-y-scroll" style="max-height: 70dvh">
        <div class="flex gap-2 flex-col justify-evenly m-3" :class="mobile ? '':'lg:flex-row'">
            <div v-for="day in Object.values(daySlot.days)" :key="`${i}-${day.id}`"
                 class=" w-100 gap-2" :class="mobile ? 'grid grid-cols-8 items-center':'flex flex-col'">
                <Button :label="mobile ? day.label.substring(0,2) : day.label"
                        :severity="day.selected ? 'primary' : 'secondary'" :rounded="mobile ? `full`:null"
                        @click="day.selected = !day.selected" :class="mobile ? '':'w-full'"></Button>
                <template v-if="day.selected">
                    <div v-for="(timeSlot, y) in day.timeslots"
                         class="border rounded-lg dark:border-neutral-700 flex flex-col gap-2 p-2" :class="mobile ? 'col-span-6' : ''">
                        <Select v-model="timeSlot.time_start" @update:model-value="timeSlot.time_end = null"
                                :placeholder="$t('forms.timeslot_start_placeholder')" fluid
                                :options="getValidStartTimes(day.timeslots, timeSlot, timeSlot.time_start)"
                                optionLabel="label" optionValue="value"></Select>
                        <Select v-if="timeSlot.time_start" v-model="timeSlot.time_end" :placeholder="$t('forms.timeslot_end_placeholder')"
                                :options="getValidEndTimes(day.timeslots, timeSlot.time_start, timeSlot, timeSlot.time_end)"
                                optionLabel="label" fluid optionValue="value"></Select>

                        <div class="flex gap-2">
                            <Button size="small" @click="timeSlot.blocking = !timeSlot.blocking"
                                    :severity="timeSlot.blocking ? 'secondary' : 'contrast'" fluid
                                    :label="timeSlot.blocking ? $t('forms.timeslot_blocking') : $t('forms.timeslot_defining')"
                                    :icon="timeSlot.blocking ? 'pi pi-ban' : 'pi pi-calendar'"
                                    :disabled="day.timeslots.length > 1"
                            ></Button>
                            <Button size="small" @click="day.timeslots.splice(y, 1)" severity="danger"
                                    icon="pi pi-trash"></Button>
                            <Button v-if="worker" size="small" severity="secondary" @click="toggleWorkers($event,timeSlot)" icon="pi pi-users"></Button>
                        </div>
                    </div>
                    <Button v-if="day.timeslots.length === 0 || day.timeslots[0].blocking" :class="mobile ? 'col-span-6 col-start-2' : ''"
                            :disabled="day.timeslots.length > 0 && !day.timeslots[day.timeslots.length-1].time_end"
                            :label="$t('forms.timeslot_add_time')" @click="addTimeSlot(day.timeslots)" icon="pi pi-plus" size="small"
                            text></Button>
                    <div v-else-if="day.timeslots.length > 0 && day.timeslots[0].time_end"
                         class="text-sm text-center opacity-75">
                        {{ $t('forms.timeslot_booking_desc', { start: day.timeslots[0].time_start, end: day.timeslots[0].time_end, interval: 30 }) }}
                    </div>
                    <div v-else-if="day.timeslots.length > 0" class="text-sm text-center opacity-75">
                        {{ $t('forms.timeslot_fill_times') }}
                    </div>
                </template>
                <p class="text-sm opacity-70 italic col-span-7" v-else-if="mobile">
                    {{ $t('forms.timeslot_no_booking') }}
                </p>
            </div>
        </div>
        <div class="p-3 border-t bg-neutral-50 dark:bg-transparent  dark:border-neutral-700 flex gap-2 items-center">
            <div v-if="i > 0" class="text-lg font-bold">
                {{ $t('forms.timeslot_limited') }}
            </div>
            <div v-else class="text-lg font-bold">
                {{ $t('forms.timeslot_default') }}
            </div>
            <template v-if="i > 0">
                <DatePicker v-model="daySlot.valid.from" :maxDate="daySlot.valid.to" :placeholder="$t('forms.timeslot_plan_from')"></DatePicker>
                <div>-</div>
                <DatePicker v-model="daySlot.valid.to" :minDate="daySlot.valid.from" :placeholder="$t('forms.timeslot_plan_to')"></DatePicker>
            </template>
            <div class="grow"></div>
            <Button v-if="(i === 0 && daySlots.length === 1) || !(daySlots.length - 1 > i)"
                    :disabled="i > 0 && (!daySlot.valid.from || !daySlot.valid.to)" severity="secondary"
                    icon="pi pi-plus" @click="addDaySlot" :label="$t('forms.timeslot_add_another_plan')"></Button>
            <Button v-if="i > 0" severity="danger" @click="daySlots.splice(i, 1)" icon="pi pi-trash"></Button>
            <slot v-if="(i === 0 && daySlots.length === 1) || !(daySlots.length - 1 > i)" ></slot>
        </div>
    </div>
    <Popover v-if="worker" ref="op">
        <div class="flex flex-col gap-2">
            <p class="text-sm font-bold">{{ $t('forms.timeslot_colleagues_in_slot') }}</p>
            <div v-for="_worker in workers" class="flex items-center gap-2 ">
                <ReverseCheckbox v-model="currentTimeSlot.exclude_workers" :value="_worker.id"></ReverseCheckbox>
                <Avatar :label="_worker.initials"></Avatar>
                <p :class="currentTimeSlot.exclude_workers?.includes(_worker.id) ? 'line-through' : ''">{{_worker.name}}</p>
            </div>
            <Button size="small" outlined severity="secondary" icon="pi pi-ban" @click="() => {
                currentTimeSlot.exclude_workers = workers.map((el) => el.id)
            }" :label="$t('forms.timeslot_remove_all')"></Button>
        </div>
    </Popover>
</template>

<style scoped lang="scss">

</style>
